import { useEffect, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Typography } from '@mui/material'
import { useAdminExcluirConfiguracaoMutation } from '../../api'
import { nomeConfere } from '../../lib/lojas-admin'
import { useAcaoUnica } from '../../lib/use-acao-unica'

export const TEXTO_EXCLUSAO_LOJA =
  'A loja sai do ar na hora e some da lista de lojas. O endereço dela fica livre para outra loja usar: se alguém pegar, ele não volta nem com o suporte. Seus produtos e pedidos no Sischef não são apagados. Para desfazer, fale com o suporte Sischef.'

interface DialogExcluirLojaProps {
  aberto: boolean
  loja: { id: number; nome: string }
  onFechar: () => void
  onExcluida: () => void
}

const MENSAGEM_PADRAO: Record<string, string> = {
  LOJA_COM_PEDIDOS_EM_ANDAMENTO: 'Esta loja tem pedidos em andamento. Conclua ou cancele esses pedidos antes de excluir.',
  SOMENTE_SUPERVISOR: 'Só o supervisor da empresa ou o suporte Sischef pode excluir uma loja.',
  NAO_ENCONTRADO: 'Esta loja não foi encontrada. Ela pode já ter sido excluída.',
}

function mensagemDoErro(erro: unknown): string {
  const dados = (erro as { data?: { codigo?: unknown; mensagem?: unknown } } | undefined)?.data
  const padrao = typeof dados?.codigo === 'string' ? MENSAGEM_PADRAO[dados.codigo] : undefined
  if (!padrao) return 'Não foi possível excluir a loja. Tente novamente.'
  return typeof dados?.mensagem === 'string' && dados.mensagem ? dados.mensagem : padrao
}

export function DialogExcluirLoja({ aberto, loja, onFechar, onExcluida }: DialogExcluirLojaProps): JSX.Element {
  const [excluir] = useAdminExcluirConfiguracaoMutation()
  const [digitado, setDigitado] = useState('')
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    if (!aberto) return
    setDigitado('')
    setErro(null)
  }, [aberto, loja.id])

  const [confirmar, excluindo] = useAcaoUnica(async () => {
    setErro(null)
    try {
      await excluir(loja.id).unwrap()
      onExcluida()
    } catch (e) {
      setErro(mensagemDoErro(e))
    }
  })

  const confere = nomeConfere(digitado, loja.nome)
  const naoConfere = digitado.trim() !== '' && !confere

  return (
    <Dialog open={aberto} onClose={excluindo ? undefined : onFechar} maxWidth="xs" fullWidth>
      <DialogTitle>Excluir a loja {loja.nome}?</DialogTitle>

      <DialogContent>
        <DialogContentText>{TEXTO_EXCLUSAO_LOJA}</DialogContentText>

        <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
          Para confirmar, digite{' '}
          <Box component="strong" sx={{ wordBreak: 'break-word' }}>
            {loja.nome}
          </Box>
        </Typography>

        <TextField
          id="confirmar-nome-loja"
          label="Digite o nome da loja para confirmar"
          fullWidth
          autoFocus
          autoComplete="off"
          value={digitado}
          onChange={(e) => setDigitado(e.target.value)}
          helperText={naoConfere ? 'Ainda não confere com o nome acima.' : ' '}
          disabled={excluindo}
        />

        {erro && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {erro}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onFechar} disabled={excluindo} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={() => void confirmar()}
          disabled={!confere || excluindo}
          variant="contained"
          color="error"
          startIcon={excluindo ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Excluir loja
        </Button>
      </DialogActions>
    </Dialog>
  )
}
